import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, Mail, Plus, Printer, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { OrderNotePreview } from "../components/admin/OrderNotePreview";
import { api, errMsg } from "../lib/api";

const EMPTY = { customer: "", occasion: "", event_date: "", delivery_time: "", contact_person: "", contact_phone: "", address: "", status: "Booking confirmed", items: [], advance: "Not paid yet (booking done)", total_amount: "", notes: "" };
const field = "h-10 rounded-lg border-[#D4AF37]/25 bg-[#190317]/60 text-sm";
const prettyDate = (d) => (/^\d{4}-\d{2}-\d{2}$/.test(d || "") ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }) : d || "");

const fromEnquiry = (e) => ({
  ...EMPTY, enquiry_id: e.id, customer: e.name, occasion: e.category_name, event_date: prettyDate(e.event_date),
  contact_person: e.name, contact_phone: e.phone, address: e.location || "",
  items: Array.isArray(e.items) && e.items.length
    ? e.items.map((i) => ({ item: i.title, size: i.spec || "", details: i.catName }))
    : [{ item: e.category_name, size: "", details: e.message || "" }],
});

const FIELDS = [["customer", "Customer"], ["occasion", "Occasion"], ["event_date", "Event date"], ["delivery_time", "Delivery time"], ["contact_person", "Contact person"], ["contact_phone", "Phone"], ["address", "Address"], ["status", "Status"], ["advance", "Advance"], ["total_amount", "Total amount"], ["notes", "Notes"]];

export default function OrderNotePage() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const [o, setO] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (id !== "new") {
      api.get(`/admin/orders/${id}`).then((r) => setO(r.data)).catch((e) => toast.error(errMsg(e)));
      return;
    }
    const eid = sp.get("enquiry");
    if (!eid) { setO({ ...EMPTY, items: [{ item: "", size: "", details: "" }] }); return; }
    api.get("/admin/enquiries").then((r) => {
      const e = (Array.isArray(r.data) ? r.data : []).find((x) => x.id === eid);
      setO(e ? fromEnquiry(e) : { ...EMPTY });
    }).catch((e) => toast.error(errMsg(e)));
  }, [id, sp]);

  if (!o) return <div className="flex min-h-screen items-center justify-center bg-[#240620] text-[#F3E5AB]"><Loader2 className="animate-spin" /></div>;

  const set = (k) => (e) => setO((s) => ({ ...s, [k]: e.target.value }));
  const setItem = (i, k, v) => setO((s) => ({ ...s, items: s.items.map((it, j) => (j === i ? { ...it, [k]: v } : it)) }));
  const save = async () => {
    if (!o.customer.trim()) return toast.error("Please add the customer name");
    setBusy("save");
    try {
      const body = { ...o, items: o.items.filter((i) => i.item.trim()) };
      const { data } = id === "new" ? await api.post("/admin/orders", body) : await api.put(`/admin/orders/${id}`, body);
      toast.success("Order note saved");
      setO(data);
      if (id === "new") navigate(`/admin/orders/${data.id}`, { replace: true });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const email = async () => {
    setBusy("email");
    try { await api.post(`/admin/orders/${id}/email`); toast.success("Order note emailed to Basketpetal@gmail.com"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  return (
    <main className="min-h-screen bg-[#240620] px-4 py-8 text-[#FFF8EF] sm:px-8" data-testid="order-note-page">
      <div className="no-print mx-auto mb-6 flex max-w-[1300px] flex-wrap items-center gap-3">
        <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-[#F3E5AB]" data-testid="order-back"><ArrowLeft size={16} /> Dashboard</Link>
        <h1 className="mr-auto font-display text-4xl">Order note</h1>
        <button onClick={save} disabled={!!busy} className="inline-flex items-center gap-2 rounded-full bg-gold-foil px-5 py-2.5 text-sm font-semibold text-[#240620]" data-testid="order-save-btn">{busy === "save" ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save</button>
        <button onClick={() => window.print()} className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/50 px-5 py-2.5 text-sm text-[#F3E5AB]" data-testid="order-print-btn"><Printer size={15} /> Print / PDF</button>
        {id !== "new" && <button onClick={email} disabled={!!busy} className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/50 px-5 py-2.5 text-sm text-[#F3E5AB]" data-testid="order-email-btn">{busy === "email" ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />} Email to me</button>}
      </div>
      <div className="mx-auto grid max-w-[1300px] gap-8 lg:grid-cols-2">
        <div className="no-print space-y-3 rounded-2xl border border-[#D4AF37]/25 bg-[#330B2E]/70 p-5">
          {FIELDS.map(([k, label]) => (
            <label key={k} className="grid grid-cols-[120px_1fr] items-center gap-3 text-sm text-[#F3E5AB]">
              {label}<Input value={o[k] || ""} onChange={set(k)} className={field} data-testid={`order-${k}-input`} />
            </label>
          ))}
          <p className="pt-3 font-cinzel text-xs tracking-[0.2em] text-[#D4AF37]">ITEMS</p>
          {o.items.map((it, i) => (
            <div key={i} className="grid grid-cols-[1fr_110px_1fr_36px] gap-2" data-testid={`order-item-${i}`}>
              <Input value={it.item} onChange={(e) => setItem(i, "item", e.target.value)} placeholder="Item" className={field} />
              <Input value={it.size} onChange={(e) => setItem(i, "size", e.target.value)} placeholder="Size / Qty" className={field} />
              <Input value={it.details} onChange={(e) => setItem(i, "details", e.target.value)} placeholder="Details" className={field} />
              <button onClick={() => setO((s) => ({ ...s, items: s.items.filter((_, j) => j !== i) }))} className="text-[#ff8fb3]" aria-label="Remove item"><Trash2 size={16} /></button>
            </div>
          ))}
          <button onClick={() => setO((s) => ({ ...s, items: [...s.items, { item: "", size: "", details: "" }] }))} className="inline-flex items-center gap-1.5 text-sm text-[#F3E5AB]" data-testid="order-add-item"><Plus size={15} /> Add item</button>
        </div>
        <OrderNotePreview o={o} />
      </div>
    </main>
  );
}
